import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { authClient } from '@frontend/api-clients/authClient';
import {
  fetchProjectCreateScope,
  getProjectProvisionStatus,
  previewProjectCreate,
  startProjectCreateAsync,
  type ProjectCreateScope,
  type StartProjectCreatePayload,
} from '@frontend/api-clients/adminProjectProvisionClient';
import { useProvisionPoll } from '../../../hooks/useProvisionPoll';
import ProvisionRunView from '../../../realm/components/orchestration/ProvisionRunView';
import PlatformServicesChecklist from '@frontend/components/projects/PlatformServicesChecklist';
import {
  normalizeProjectServices,
  resolveInstalledProjectServices,
  type ProjectServiceId,
} from '@frontend/components/projects/projectServices';
import { usePublicIntegration } from '@frontend/context/PublicIntegrationContext';
import { PROJECT_SLUG_RE, suggestProjectSlug } from './projectSlug';

type WizardPhase = 'form' | 'review' | 'running';

export interface CreateProjectOrchestrationWizardProps {
  initialEcosystemId?: string;
  initialParentProjectId?: string;
  onProjectCreated?: (projectId: string) => void;
}

export default function CreateProjectOrchestrationWizard({
  initialEcosystemId = '',
  initialParentProjectId = '',
  onProjectCreated,
}: CreateProjectOrchestrationWizardProps) {
  const integration = usePublicIntegration();
  const [phase, setPhase] = useState<WizardPhase>('form');
  const [scope, setScope] = useState<ProjectCreateScope | null>(null);
  const [scopeError, setScopeError] = useState('');
  const [scopeLoading, setScopeLoading] = useState(true);

  const [ecosystemId, setEcosystemId] = useState(initialEcosystemId);
  const [parentProjectId, setParentProjectId] = useState(initialParentProjectId);
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  /** Once the operator edits the slug field, stop auto-updating it from the display name. */
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [services, setServices] = useState<ProjectServiceId[]>([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [openprojectUrl, setOpenprojectUrl] = useState('');
  const [nextcloudUrl, setNextcloudUrl] = useState('');
  const [giteaUrl, setGiteaUrl] = useState('');
  const [clientReferenceId, setClientReferenceId] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState('');

  const [plannedSteps, setPlannedSteps] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const completionNotified = useRef(false);

  useEffect(() => {
    const load = async () => {
      setScopeLoading(true);
      setScopeError('');
      try {
        const t = authClient.getToken();
        if (!t) throw new Error('You must be signed in.');
        const s = await fetchProjectCreateScope(t);
        setScope(s);
        if (s.ecosystems.length > 0) {
          const preferred =
            s.ecosystems.find((row) => row.id === initialEcosystemId) || s.ecosystems[0];
          setEcosystemId((current) => current || preferred.id);
          setServices((current) =>
            current.length > 0 ? current : normalizeProjectServices(preferred.services),
          );
        }
      } catch (e) {
        setScopeError(e instanceof Error ? e.message : 'Failed to load create scope');
      } finally {
        setScopeLoading(false);
      }
    };
    load();
  }, [initialEcosystemId]);

  const parentChoices = useMemo(() => {
    if (!scope) return [];
    return scope.parentProjects.filter((p) => !ecosystemId || p.ecosystemId === ecosystemId);
  }, [scope, ecosystemId]);

  const parentRequired = Boolean(scope?.parentRequired);
  const selectedEcosystem = useMemo(
    () => scope?.ecosystems.find((row) => row.id === ecosystemId),
    [scope, ecosystemId],
  );
  const availableServices = useMemo(
    () =>
      resolveInstalledProjectServices(
        selectedEcosystem?.availableServices,
        integration.installedServices,
      ),
    [selectedEcosystem?.availableServices, integration.installedServices],
  );
  // Blanket create rights (patriot / realm / scoped eco admin) imply package activation.
  const canActivatePackages =
    selectedEcosystem?.canAddEcosystemServices ??
    selectedEcosystem?.canAddRealmServices ??
    Boolean(scope?.eligible && !scope?.parentRequired);
  const canAddRealmServices = selectedEcosystem?.canAddRealmServices ?? canActivatePackages;
  const canAddEcosystemServices =
    selectedEcosystem?.canAddEcosystemServices ?? canActivatePackages;

  const payload = (): StartProjectCreatePayload => {
    const base: StartProjectCreatePayload = {
      id: id.trim(),
      ecosystemId,
      name: name.trim(),
      description: description.trim() || undefined,
      services,
      includeRepository: services.includes('gitea'),
      parentProjectId: parentProjectId.trim() || undefined,
    };
    if (advancedOpen) {
      if (openprojectUrl.trim()) base.openprojectUrl = openprojectUrl.trim();
      if (nextcloudUrl.trim()) base.nextcloudUrl = nextcloudUrl.trim();
      if (giteaUrl.trim()) base.giteaUrl = giteaUrl.trim();
      if (clientReferenceId.trim()) base.clientReferenceId = clientReferenceId.trim();
    }
    return base;
  };

  const { status, error: pollError, polling } = useProvisionPoll({
    token,
    correlationId,
    fetchStatus: getProjectProvisionStatus,
    enabled: phase === 'running' && Boolean(correlationId),
  });

  useEffect(() => {
    if (
      (status?.runState !== 'completed' && status?.runState !== 'partial') ||
      completionNotified.current
    ) {
      return;
    }
    completionNotified.current = true;
    onProjectCreated?.(String(status.projectId || id).trim());
  }, [status, id, onProjectCreated]);

  const validateForm = (): string | null => {
    if (!scope?.eligible) return 'You do not have permission to create projects in any ecosystem.';
    const slug = id.trim();
    if (!slug) return 'Project id is required.';
    if (!PROJECT_SLUG_RE.test(slug)) {
      return 'Project id must be a lowercase slug (letters, digits, hyphens only).';
    }
    if (!ecosystemId) return 'Select an ecosystem.';
    if (!scope.ecosystems.some((e) => e.id === ecosystemId)) {
      return 'Selected ecosystem is not in your allowed list.';
    }
    if (!name.trim()) return 'Display name is required.';
    if (name.trim().length > 120) return 'Display name must be 120 characters or fewer.';
    if (description.length > 2000) return 'Description must be 2000 characters or fewer.';
    if (parentRequired || parentProjectId.trim()) {
      const pp = parentProjectId.trim();
      if (!pp) return 'Select a parent project (required for maintainers adding sub-projects).';
      const found = scope.parentProjects.find((p) => p.id === pp);
      if (!found) return 'Parent project must be picked from your allowed list.';
      if (found.ecosystemId !== ecosystemId) return 'Parent project must belong to the selected ecosystem.';
    }
    return null;
  };

  const onReview = async (event: FormEvent) => {
    event.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const t = authClient.getToken();
      if (!t) throw new Error('You must be signed in.');
      const preview = await previewProjectCreate(t, payload());
      setPlannedSteps(preview.plannedSteps || []);
      setPhase('review');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Preview failed');
    } finally {
      setBusy(false);
    }
  };

  const onStart = async () => {
    setBusy(true);
    setError('');
    try {
      const t = authClient.getToken();
      if (!t) throw new Error('You must be signed in.');
      const opts = advancedOpen && idempotencyKey.trim() ? { idempotencyKey: idempotencyKey.trim() } : undefined;
      const start = await startProjectCreateAsync(t, payload(), opts);
      setToken(t);
      setCorrelationId(start.correlationId);
      setPhase('running');
    } catch (e) {
      const err = e as Error & { correlationId?: string };
      if (err.correlationId) setCorrelationId(err.correlationId);
      setError(err.message || 'Failed to start provisioning');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setPhase('form');
    setPlannedSteps([]);
    setError('');
    setCorrelationId(null);
    setToken(null);
    setId('');
    setName('');
    setSlugTouched(false);
    setDescription('');
    setParentProjectId(initialParentProjectId);
    setAdvancedOpen(false);
    setOpenprojectUrl('');
    setNextcloudUrl('');
    setGiteaUrl('');
    setClientReferenceId('');
    setIdempotencyKey('');
    const preferred =
      scope?.ecosystems.find((row) => row.id === initialEcosystemId) || scope?.ecosystems[0];
    if (preferred) {
      setEcosystemId(preferred.id);
      setServices(normalizeProjectServices(preferred.services));
    }
    completionNotified.current = false;
  };

  if (scopeLoading) {
    return <p className="ecosystem-description">Loading allowed ecosystems…</p>;
  }
  if (scopeError) {
    return <div className="settings-message settings-error">{scopeError}</div>;
  }
  if (!scope?.eligible) {
    return (
      <p className="ecosystem-description">
        You cannot create orchestrated portal projects with the current account. Ask a realm or ecosystem administrator, or maintain a parent project first.
      </p>
    );
  }

  if (phase === 'running') {
    return (
      <div className="admin-orch-wizard">
        <ProvisionRunView
          title="Project provision status"
          status={status}
          pollError={pollError}
          loading={polling && !status}
        />
        {status && (status.runState === 'completed' || status.runState === 'failed' || status.runState === 'partial') ? (
          <div className="admin-create-user-actions">
            <button type="button" className="btn btn-secondary" onClick={reset}>
              {status.runState === 'completed' ? 'Create another' : 'Start over'}
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  if (phase === 'review') {
    const p = payload();
    return (
      <div className="admin-orch-wizard">
        <h4 className="admin-orch-subtitle">Review planned steps</h4>
        <ul className="admin-sync-status-list">
          {plannedSteps.map((step) => (
            <li key={step}>
              <span className="admin-sync-status-label">{step.replace(/_/g, ' ')}</span>
            </li>
          ))}
        </ul>
        <dl className="admin-orch-summary">
          <dt>Ecosystem</dt>
          <dd>
            <code className="admin-inline-code">{p.ecosystemId}</code>
          </dd>
          <dt>Name</dt>
          <dd>{p.name}</dd>
          <dt>Project id</dt>
          <dd>
            <code className="admin-inline-code">{p.id}</code>
          </dd>
          {p.parentProjectId ? (
            <>
              <dt>Parent project</dt>
              <dd>
                <code className="admin-inline-code">{p.parentProjectId}</code>
              </dd>
            </>
          ) : null}
          <dt>Services</dt>
          <dd>{p.services?.join(', ') || 'None'}</dd>
        </dl>
        {error ? (
          <div className="settings-message settings-error" role="alert">
            {error}
          </div>
        ) : null}
        <p className="ecosystem-description">
          Provision runs asynchronously. After you confirm, progress updates appear below (polling the portal status endpoint).
        </p>
        <div className="admin-create-user-actions">
          <button type="button" className="btn btn-primary" disabled={busy} onClick={onStart}>
            {busy ? 'Starting…' : 'Start provisioning'}
          </button>
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setPhase('form')}>
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="admin-orch-wizard" onSubmit={onReview}>
      <p className="ecosystem-description">
        Registers the portal catalog row and queues downstream provisioning (OpenProject, Gitea, Nextcloud,
        Woodpecker when enabled). Progress uses <strong>polling</strong> on the orchestration status URL (see infra
        plan for backlog SSE/WebSocket).
      </p>

      <label className="admin-orch-field" htmlFor="proj-orch-eco">
        <span>Ecosystem</span>
        <select
          id="proj-orch-eco"
          value={ecosystemId}
          required
          onChange={(event) => {
            const nextId = event.target.value;
            setEcosystemId(nextId);
            setParentProjectId('');
            const next = scope.ecosystems.find((row) => row.id === nextId);
            setServices(normalizeProjectServices(next?.services));
          }}
        >
          {scope.ecosystems.map((e) => (
            <option key={e.id} value={e.id}>
              {e.displayName} ({e.id})
            </option>
          ))}
        </select>
      </label>

      {parentRequired && parentChoices.length === 0 ? (
        <div className="settings-message settings-error" role="alert">
          No parent projects are available in this ecosystem with your maintainer permissions. You need project
          maintainer or admin role on a parent project first.
        </div>
      ) : null}

      <label className="admin-orch-field">
        <span>Parent project (sub-project lineage)</span>
        <select
          value={parentProjectId}
          required={parentRequired}
          onChange={(event) => setParentProjectId(event.target.value)}
        >
          {!parentRequired ? <option value="">None</option> : <option value="">Select…</option>}
          {parentChoices.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.id})
            </option>
          ))}
        </select>
        {parentRequired ? (
          <span className="ecosystem-description">Required for project maintainers creating sub-projects.</span>
        ) : (
          <span className="ecosystem-description">Optional linkage stored in portal SoT (parentProjectId).</span>
        )}
      </label>

      <label className="admin-orch-field" htmlFor="proj-orch-name">
        <span>Display name</span>
        <input
          id="proj-orch-name"
          type="text"
          maxLength={120}
          value={name}
          onChange={(event) => {
            const nextName = event.target.value;
            setName(nextName);
            if (!slugTouched) {
              setId(suggestProjectSlug(nextName));
            }
          }}
          required
        />
      </label>

      <label className="admin-orch-field" htmlFor="proj-orch-id">
        <span>Project id (slug)</span>
        <input
          id="proj-orch-id"
          type="text"
          value={id}
          onChange={(event) => {
            setSlugTouched(true);
            setId(event.target.value);
          }}
          required
        />
        {!slugTouched && name.trim() ? (
          <span className="ecosystem-description">Suggested from the display name; edit to override.</span>
        ) : null}
      </label>

      <label className="admin-orch-field" htmlFor="proj-orch-desc">
        <span>Description</span>
        <textarea
          id="proj-orch-desc"
          rows={3}
          maxLength={2000}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </label>

      <PlatformServicesChecklist
        idPrefix="create-project-service"
        packageCatalog={selectedEcosystem?.packageCatalog}
        availableServices={availableServices}
        realmServices={selectedEcosystem?.realmServices}
        ecosystemServices={selectedEcosystem?.services}
        canAddRealmServices={canAddRealmServices}
        canAddEcosystemServices={canAddEcosystemServices}
        selectedServices={services}
        onChange={setServices}
      />

      <fieldset className="admin-orch-fieldset">
        <button type="button" className="btn btn-secondary" onClick={() => setAdvancedOpen((v) => !v)}>
          {advancedOpen ? 'Hide advanced' : 'Advanced'}
        </button>
        {advancedOpen ? (
          <div style={{ marginTop: '0.75rem' }}>
            <label className="admin-orch-field" htmlFor="proj-orch-op">
              <span>openprojectUrl override (portal path)</span>
              <input id="proj-orch-op" type="text" value={openprojectUrl} onChange={(e) => setOpenprojectUrl(e.target.value)} />
            </label>
            <label className="admin-orch-field" htmlFor="proj-orch-nc">
              <span>nextcloudUrl override</span>
              <input id="proj-orch-nc" type="text" value={nextcloudUrl} onChange={(e) => setNextcloudUrl(e.target.value)} />
            </label>
            <label className="admin-orch-field" htmlFor="proj-orch-gt">
              <span>giteaUrl override</span>
              <input id="proj-orch-gt" type="text" value={giteaUrl} onChange={(e) => setGiteaUrl(e.target.value)} />
            </label>
            <label className="admin-orch-field" htmlFor="proj-orch-cref">
              <span>clientReferenceId</span>
              <input id="proj-orch-cref" type="text" value={clientReferenceId} onChange={(e) => setClientReferenceId(e.target.value)} />
            </label>
            <label className="admin-orch-field" htmlFor="proj-orch-idem">
              <span>Idempotency-Key (header)</span>
              <input id="proj-orch-idem" type="text" value={idempotencyKey} onChange={(e) => setIdempotencyKey(e.target.value)} />
            </label>
          </div>
        ) : null}
      </fieldset>

      {error ? (
        <div className="settings-message settings-error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="admin-create-user-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Preparing preview…' : 'Review'}
        </button>
      </div>
    </form>
  );
}
